<template>
<div class="card">
    <div class="card-header">Draft Posting</div>
    <div class="card-body">
        <form-summary :validator="$v" ref="errorContainer" id="errorContainer"/>
        <BaseStatusHelp lifecycleStatus="07"/>
        
        <b-row v-if="isHRTransactions">
            <b-col>
                <b-alert show>This request is currently having a PCN created by HR Transactions. Once PCN is assigned, this request can progress.</b-alert>
            </b-col>
        </b-row>

        <b-form @submit.stop.prevent="onSubmit" v-else>
            <b-row>
                <b-col>
                    <LifecycleCheckList lifecycleStatus="07" :requestId="request.id"
                        v-on:checklist-update="updateChecklistStatus($event)" ref="checklist"/>
                </b-col>
            </b-row>

            <b-row>
                <b-col>
                    <BaseTextInput v-model="request.jobId" 
                        :v="$v.request.jobId"
                        slug="jobId"
                        label="Recruiting Solutions Job ID"
                        :required="$v.request.jobId.$params.required"/>
                </b-col>
            </b-row>

            <b-row>
                <b-col cols="12" lg="3">
                    <BaseDateInput v-model="request.postingOpenDate" 
                        :v="$v.request.postingOpenDate"
                        label="Posting Open Date"
                        slug="open-date"/>
                </b-col>
                <b-col cols="12" lg="3">
                    <BaseDateInput v-model="request.postingCloseDate" 
                        :v="$v.request.postingCloseDate"
                        label="Posting Close Date"
                        slug="close-date"/>                        
                </b-col>
                <b-col cols="12" lg="3">
                  <BaseSelector v-model="request.howPosted"
                                :v="$v.request.howPosted"
                                label="How Posted"
                                slug="how-posted"
                                :options=howPostedOptions />
                </b-col>
            </b-row>

            <b-row>
                <b-col>
                    <BaseParagraphInput v-model="request.draftPostingComments"
                        :v="$v.request.draftPostingComments" 
                        slug="draftPostingComments"
                        label="Staffing Specialist Comments/Questions"/>
                </b-col>
            </b-row>

            <b-row class="my-4" v-if="request.postingFeedback">
                <b-col>
                    <BaseTextOutput :value="request.postingFeedback"
                                  label="Feedback from Requester"/>
                </b-col>
            </b-row>
            
            <div>

            <b-button type="button" variant="primary" @click="onSubmitForReview" :disabled="(request.requestTypeValue?.code === '10' && !hasBeenReviewed)"
                    v-if="request.requestTypeValue?.code !== '10' || request.draftPostAudit !== true">Submit Draft Posting for Review</b-button>

            <b-button type="button" variant="primary" @click="onSubmitApproved"
                    v-if="request.draftPostAudit === true && isApprover">Approve Draft Posting</b-button>
            <b-button type="button" variant="primary" @click="onSubmitReturn"
                    v-if="request.draftPostAudit === true && isApprover">Return Draft Posting to Staffing Specialist</b-button>

            <b-button type="button" variant="outline-primary" @click="save" v-if="request.salaryReviewAudit == null">Save</b-button>

            <b-button type="button" variant="outline-primary" v-if="request.draftPostAudit !== true && request.requestTypeValue?.code === '10'"
                    :disabled="!hasReqAttachments()" @click="returnToRequester">Route to Hiring Supervisor</b-button>
            </div>
        </b-form>
    </div>
</div>

</template>

<script>
import axios from "axios"
import { required, email, minLength, maxLength, numeric, requiredUnless } from 'vuelidate/lib/validators'
import LifecycleCheckList from '@/components/lifecycles/LifecycleCheckList'
import _ from "lodash"
import {roleService} from "@/mixins/roleService";


export default {
    components: {
        LifecycleCheckList,
    },
    props: {
        request: Object
    },
    mixins: [
      roleService
    ],
    data() {
        return {
            checklistComplete: false,
            comments: '',
            saveOnly: false,
            needReviewApproval: false,
            hasBeenReviewed: false,
            attachmentReqs: [],
            howPostedOptions:[
                { value: 'Statewide', text: 'Statewide' },
                { value: 'Competitive', text: 'Competitive' },
                { value: 'Agency Only', text: 'Agency Only' },
                { value: 'Non-Competitive', text: 'Non-Competitive' },
            ]
        }
    },
    computed: {       
        isHRTransactions: function() {
            return this.request.transactionQueueStatus === 1
        },
        isApprover: function() {
            return (this.request.draftPostAudit === true && this.isDraft())
        },
    },
    created() {
      if(this.request.needReviewApproval != null) {
        this.needReviewApproval = this.request.needReviewApproval
      }
      this.findReviewStatus()
      this.findReqAttachments()
    },
    
    mounted: function() {
        this.autosave()
    },
    
    beforeDestroy() {
       clearInterval(this.autosave_id)
    },

     validations: {
         checklistComplete: { checked: value => value === true },
         comments: { required: false, maxLength: maxLength(4000) },
         request: {
             jobId: {required, maxLength: maxLength(10) },   
             draftPostingComments: { required: false, maxLength: maxLength(4000) },    
             postingOpenDate: { 
                 required: requiredUnless(function() {
                    return _.includes(['06'], this.request.requestTypeValue.code)
                }),
              },
             postingCloseDate: {
                required: requiredUnless(function() {
                    return _.includes(['06'], this.request.requestTypeValue.code)
                }),
            },
            howPosted:{required: false},      
         }
     },
    methods: {
        updateChecklistStatus(b) {
            this.checklistComplete = b
        },

        autosave() {          
            this.autosave_id = setInterval(() => {
                this.save()
            }, 300000);
        },

        async save() {
            this.saveOnly = true
            await this.onSubmitForReviewWithSaveOnly(this.saveOnly)
            this.saveOnly = false
        },

        onSubmitForReview(){
          this.onSubmitForReviewWithSaveOnly(false);
        },

        async onSubmitForReviewWithSaveOnly(saveOnly) {
             if (!saveOnly) {
                this.$refs.checklist.emitCheckListStatus()
                this.$v.$touch();
                if (this.$v.$invalid) {
                    this.$nextTick(() => {
                        document.getElementById('form-container').scrollIntoView()
                    })
                    return
                }
            }

            let payload = {
                requestId: this.request.id,
                jobId: this.request.jobId,
                postingOpenDate: this.request.postingOpenDate,
                postingCloseDate: this.request.postingCloseDate,
                howPosted: this.request.howPosted,
                comments: this.request.draftPostingComments,
                advanceNextLifecycle: false,
                nextLifecycle: '08',
                saveOnly: saveOnly,
            }

            await axios.post(`${process.env.VUE_APP_API_BASE_URL}/request/draftPosting`, payload)
                .then(response => {
                  if(saveOnly === false){
                    this.showRootToast("Draft Posting has been submitted to HR Supervisor.", "Success", "success")
                  }
                  else{
                    this.showRootToast("HRM Request Has Been Saved", "Success", "success")
                  }
                    this.$router.push('/home')
            })

        },

        onSubmitReturn() {
            if (!this.saveOnly) {
                this.$refs.checklist.emitCheckListStatus()
                this.$v.$touch();
                if (this.$v.$invalid) {
                    this.$nextTick(() => {
                        document.getElementById('form-container').scrollIntoView()
                    })
                    return
                }
            }
            
            let payload = {
                requestId: this.request.id,
                jobId: this.request.jobId,
                postingOpenDate: this.request.postingOpenDate,
                postingCloseDate: this.request.postingCloseDate,
                howPosted: this.request.howPosted,
                comments: this.request.draftPostingComments,
                advanceNextLifecycle: false,
                nextLifecycle: '08',
                saveOnly: true,
            }

            axios.post(`${process.env.VUE_APP_API_BASE_URL}/request/draftPosting/return`, payload)
                .then(response => {                       
                    this.showRootToast("HRM Request Has Been Returned to Staffing Specialist", "Success", "success")
                    this.$router.push('/home')
            })
        },

        onSubmitApproved() {
            if (!this.saveOnly) {
                this.$refs.checklist.emitCheckListStatus()
                this.$v.$touch();
                if (this.$v.$invalid) {
                    this.$nextTick(() => {
                        document.getElementById('form-container').scrollIntoView()
                    })
                    return
                }
            }

            let payload = {
                requestId: this.request.id,
                jobId: this.request.jobId,
                postingOpenDate: this.request.postingOpenDate,
                postingCloseDate: this.request.postingCloseDate,
                howPosted: this.request.howPosted,
                comments: this.request.draftPostingComments,
                advanceNextLifecycle: true,
                saveOnly: false,
                nextLifecycle: '08',
            }


            axios.post(`${process.env.VUE_APP_API_BASE_URL}/request/draftPosting/approve`, payload)
                  .then(response => {                       
                    this.showRootToast("HRM Request Has Been Updated", "Success", "success")
                    if (payload.advanceNextLifecycle === true) {
                        this.$router.push('/home')
                    } 
                  })
                  
        },

        async returnToRequester() {
            await this.save()
            let payload = {
                requestId: this.request.id,
                nextLifecycle: '34'
            }
        
            axios.post(`${process.env.VUE_APP_API_BASE_URL}/manager/return/requester`, payload)
              .then(response => {
                this.showRootToast("HRM Request Has Been Sent to Reqester", "Success", "success")
                this.$router.push('/home')
              })
        },

        findReviewStatus() {        
            axios.get(`${process.env.VUE_APP_API_BASE_URL}/lifecycle/${this.request.id}/34`)
              .then(response => {
                  if (response.data.message === "0") {
                      this.hasBeenReviewed = false
                  } else {
                      this.hasBeenReviewed = true
                  }
              })
        },

        showRootToast(title, message, type) {
            this.$root.$bvToast.toast(message, {
                  title: title,
                  toaster: 'b-toaster-top-center',
                  solid: true,
                  variant: type,
                  appendToast: true
                })
        },

      hasReqAttachments() {
        if (this.attachmentReqs.length>0) {
          req:
              for (let i=0; i<this.attachmentReqs.length; i++) {
                for (let j=0; j<this.request.attachments.length; j++) {
                  if (this.attachmentReqs[i].attachmentTypeValue.code == this.request.attachments[j].type) {
                    break req;
                  }
                }
                return false;
              }
          return true;
        }
        return true;
      },

      findReqAttachments() {
        axios.post(`${process.env.VUE_APP_API_BASE_URL}/attachment/requirements`, {typeCode: this.request.requestTypeValue.code, statusCode: this.request.requestStatusValue.code})
            .then(response => {
              this.attachmentReqs = response.data.filter((req) => req.required == true);
            })
      }
    }   
}
</script>